import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, addDoc, doc, updateDoc, deleteDoc, query, orderBy, where } from 'firebase/firestore';
import { dbChangelogs } from '../../App';
import { UserContext } from '../../universal/context/UserContext';
import { useLoader } from '../../universal/context/LoaderContext';
import { useNotification } from '../../universal/context/NotificationContext';

const allowedEmails = [
  'sonny@dreamlead.me',
  'kristoffer@dreamlead.me',
  'chris@dreamlead.me',
  'c.azzopardi1999@gmail.com'
];

const statusColors = {
  Live: '#48BB78', // green
  Testing: '#F6AD55', // orange
  Development: '#4299E1', // blue
  Idea: '#A0AEC0', // gray
};

interface Changelog {
  id: string;
  name: string;
  description: string;
  category: string;
  notes: string;
  dateDeployed: string;
  createdBy: string;
  lastEditedBy: string;
  affectedSites: string;
  createdAt: number;
  lastUpdatedAt?: number;
  status: 'Testing' | 'Development' | 'Live' | 'Idea';
}

interface ChangelogCategory {
  id: string;
  name: string;
}

const ChangelogsPage: React.FC = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();

  const [changelogs, setChangelogs] = useState<Changelog[]>([]);
  const [categories, setCategories] = useState<ChangelogCategory[]>([]);
  const [filteredChangelogs, setFilteredChangelogs] = useState<Changelog[]>([]);

  const [showPopup, setShowPopup] = useState(false);
  const [editingChangelog, setEditingChangelog] = useState<Changelog | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [notes, setNotes] = useState('');
  const [dateDeployed, setDateDeployed] = useState('');
  const [affectedSites, setAffectedSites] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [status, setStatus] = useState<'Testing' | 'Development' | 'Live' | 'Idea'>('Idea');
  const [expandedNotes, setExpandedNotes] = useState<{ [key: string]: boolean }>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const toggleNotes = (id: string) => {
    setExpandedNotes(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  useEffect(() => {
    if (user && user.email && !allowedEmails.includes(user.email)) {
      navigate('/');
      return;
    }
    fetchChangelogs();
    fetchCategories();
  }, [user, navigate]);

  useEffect(() => {
    let filtered = changelogs;

    if (searchQuery) {
      filtered = filtered.filter(c =>
        c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.affectedSites.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (selectedCategory) {
      filtered = filtered.filter(c => c.category === selectedCategory);
    }

    setFilteredChangelogs(filtered);
  }, [searchQuery, selectedCategory, changelogs]);

  const fetchChangelogs = async () => {
    showLoader();
    try {
      const q = query(collection(dbChangelogs, 'changelogs'), orderBy('dateDeployed', 'desc'));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Changelog));
      setChangelogs(data);
    } catch (error) {
      console.error(error);
      showNotification('Failed to load changelogs!', 'error');
    } finally {
      hideLoader();
    }
  };

  const fetchCategories = async () => {
    try {
      const snapshot = await getDocs(collection(dbChangelogs, 'categories'));
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as ChangelogCategory));
      setCategories(data);
    } catch (error) {
      console.error(error);
      showNotification('Failed to load categories!', 'error');
    }
  };

  const handleAddNewChangelog = () => {
    setEditingChangelog(null);
    setName('');
    setDescription('');
    setCategory('');
    setNewCategory('');
    setNotes('');
    setDateDeployed(new Date().toISOString().split('T')[0]);
    setAffectedSites('');
    setStatus('Idea');
    setShowPopup(true);
  };

  const handleEditChangelog = (changelog: Changelog) => {
    setEditingChangelog(changelog);
    setName(changelog.name);
    setDescription(changelog.description);
    setCategory(changelog.category);
    setNewCategory('');
    setNotes(changelog.notes);
    setDateDeployed(changelog.dateDeployed);
    setAffectedSites(changelog.affectedSites);
    setStatus(changelog.status || 'Idea');
    setShowPopup(true);
  };
  
  const handleDeleteChangelog = (id: string) => {
    setDeletingId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deletingId) return;
    showLoader();
    try {
        await deleteDoc(doc(dbChangelogs, 'changelogs', deletingId));
        showNotification('Changelog deleted successfully!', 'success');
        fetchChangelogs();
    } catch (error) {
        console.error('Error deleting changelog:', error);
        showNotification('Failed to delete changelog.', 'error');
    } finally {
        hideLoader();
        setShowDeleteConfirm(false);
        setDeletingId(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !description) {
      showNotification('Name and Description are required!', 'error');
      return;
    }
    showLoader();

    let finalCategory = category;
    if (newCategory) {
      try {
        const categoryRef = await addDoc(collection(dbChangelogs, 'categories'), { name: newCategory });
        finalCategory = newCategory;
        setCategories([...categories, { id: categoryRef.id, name: newCategory }]);
        setNewCategory('');
      } catch (error) {
        console.error(error);
        showNotification('Failed to create new category!', 'error');
        hideLoader();
        return;
      }
    }

    const commonData = {
      name,
      description,
      category: finalCategory,
      notes,
      dateDeployed,
      affectedSites,
      status,
      lastEditedBy: user?.name || 'Unknown',
    };

    try {
      if (editingChangelog) {
        const changelogRef = doc(dbChangelogs, 'changelogs', editingChangelog.id);
        await updateDoc(changelogRef, {
          ...commonData,
          lastUpdatedAt: Date.now(),
        });
        showNotification('Changelog updated successfully!', 'success');
      } else {
        await addDoc(collection(dbChangelogs, 'changelogs'), {
          ...commonData,
          createdBy: user?.name || 'Unknown',
          createdAt: Date.now(),
        });
        showNotification('Changelog created successfully!', 'success');
      }
      fetchChangelogs();
      setShowPopup(false);
    } catch (error) {
      console.error(error);
      showNotification('Failed to save changelog!', 'error');
    } finally {
      hideLoader();
    }
  };
  
    if (!user || !user.email || !allowedEmails.includes(user.email)) {
        return null; // Or a loading spinner
    }

  return (
    <div className="dashpanel">
      <div className="main">
        <div className="head">
          <h1>Changelogs</h1>
          <button className="termsbuttonstyleadd" onClick={handleAddNewChangelog}>
            New Changelog
          </button>
          <div style={{ display: 'flex', marginBottom: '20px', marginTop: '20px' }}>
            <input
              type="text"
              placeholder="Search changelogs..."
              value={searchQuery}
              style={{marginTop: '0', marginRight: '10px',padding: '8px', width: '300px' }} 
              onChange={e => setSearchQuery(e.target.value)}
            />
            <select
              value={selectedCategory}
              onChange={e => setSelectedCategory(e.target.value)}
              style={{ padding: '8px', width: '200px' }}
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.name}>{cat.name}</option>
              ))}
            </select>
          </div>
          <div className="automations-table" style={{ marginTop: '20px' }}>
            {filteredChangelogs.length === 0 ? (
              <p>No changelogs found.</p>
            ) : (
              filteredChangelogs.map(changelog => {
                const isExpanded = expandedNotes[changelog.id];
                const notesAreLong = changelog.notes && changelog.notes.length > 200;

                return (
                  <div key={changelog.id} style={{
                    border: '2px solid lightgray',
                    maxWidth: '99%',
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    padding: '24px',
                    marginBottom: '20px',
                    borderLeft: '5px solid #4299E1'
                  }}>
                    {/* Card Header */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      paddingBottom: '16px',
                      borderBottom: '1px solid #e2e8f0',
                      marginBottom: '16px'
                    }}>
                      <div style={{ flex: 1 }}>
                        <h3 style={{ margin: 0, fontSize: '1.4rem', fontWeight: '600', color: '#2d3748' }}>{changelog.name}</h3>
                        <div style={{ marginTop: '10px' }}>
                          {changelog.category && (
                            <span style={{
                              display: 'inline-block',
                              backgroundColor: '#e2e8f0',
                              color: '#4a5568',
                              padding: '4px 10px',
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                              marginRight: '10px'
                            }}>{changelog.category}</span>
                          )}
                          {changelog.status && (
                            <span style={{
                              display: 'inline-block',
                              backgroundColor: statusColors[changelog.status] || statusColors.Idea,
                              color: '#fff',
                              padding: '4px 10px',
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                            }}>{changelog.status}</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <button
                          onClick={() => handleEditChangelog(changelog)}
                          style={{
                            background: '#3182ce',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '8px 12px',
                            cursor: 'pointer',
                            marginRight: '8px',
                            fontWeight: 500
                          }}>Edit</button>
                        <button
                          onClick={() => handleDeleteChangelog(changelog.id)}
                          style={{
                            background: '#e53e3e',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '8px 12px',
                            cursor: 'pointer',
                            fontWeight: 500
                          }}>Delete</button>
                      </div>
                    </div>

                    {/* Card Body */}
                    <div style={{ color: '#4a5568', lineHeight: 1.6 }}>
                      <p style={{ margin: '0 0 16px 0' }}>{changelog.description}</p>
                      {changelog.notes && (
                        <div style={{ background: '#f7fafc', padding: '12px', borderRadius: '6px', marginBottom: '12px' }}>
                          <strong style={{ color: '#2d3748' }}>Notes:</strong>
                          <div style={{
                            maxHeight: isExpanded ? '1000px' : '60px',
                            overflow: 'hidden',
                            transition: 'max-height 0.5s ease-in-out',
                          }}>
                            <p style={{ margin: '4px 0 0 0', whiteSpace: 'pre-wrap' }}>
                              {changelog.notes}
                            </p>
                          </div>
                          {notesAreLong && (
                            <button
                              onClick={() => toggleNotes(changelog.id)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#3182ce',
                                cursor: 'pointer',
                                padding: '4px 0 0 0',
                                marginTop: '8px',
                                fontWeight: '600',
                                fontSize: '0.9rem'
                              }}
                            >
                              {isExpanded ? 'Read Less' : 'Read More'}
                            </button>
                          )}
                        </div>
                      )}
                      {changelog.affectedSites && (
                        <div style={{ background: '#f7fafc', padding: '12px', borderRadius: '6px' }}>
                          <strong style={{ color: '#2d3748' }}>Affected Sites:</strong>
                          <p style={{ margin: '4px 0 0 0' }}>{changelog.affectedSites}</p>
                        </div>
                      )}
                    </div>

                    {/* Card Footer */}
                    <div style={{
                      marginTop: '20px',
                      paddingTop: '16px',
                      borderTop: '1px solid #e2e8f0',
                      fontSize: '0.8rem',
                      color: '#718096',
                      display: 'flex',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap'
                    }}>
                      <span><strong>Created by:</strong> {changelog.createdBy}</span>
                      <span><strong>Deployed:</strong> {new Date(changelog.dateDeployed).toLocaleDateString()}</span>
                      {changelog.lastUpdatedAt && (
                         <span><strong>Last updated:</strong> {new Date(changelog.lastUpdatedAt).toLocaleDateString()} by {changelog.lastEditedBy}</span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {showPopup && (
          <div className="edit-overlay z-ind-25" style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
            <div style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '8px', width: '600px', maxWidth: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
              <h3>{editingChangelog ? 'Edit Changelog' : 'Create Changelog'}</h3>
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '16px' }}>
                  <label>Name</label>
                  <input type="text" value={name} onChange={e => setName(e.target.value)} style={{ width: '100%', padding: '8px' }} />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label>Description</label>
                  <textarea value={description} onChange={e => setDescription(e.target.value)} style={{ width: '100%', padding: '8px', minHeight: '80px' }} />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label>Category</label>
                  <select value={category} onChange={e => setCategory(e.target.value)} style={{ width: '100%', padding: '8px' }}>
                    <option value="">-- Select Category --</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.name}>{cat.name}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="Or add new category"
                    value={newCategory}
                    onChange={e => setNewCategory(e.target.value)}
                    style={{ width: '100%', padding: '8px', marginTop: '8px' }}
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label>Status</label>
                  <select value={status} onChange={e => setStatus(e.target.value as any)} style={{ width: '100%', padding: '8px' }}>
                    <option value="Idea">Idea</option>
                    <option value="Development">Development</option>
                    <option value="Testing">Testing</option>
                    <option value="Live">Live</option>
                  </select>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label>Notes</label>
                  <textarea value={notes} onChange={e => setNotes(e.target.value)} style={{ width: '100%', padding: '8px' }} />
                </div>
                <div style={{ marginBottom: '16px' }}>
                    <label>Affected Sites (comma-separated)</label>
                    <input type="text" value={affectedSites} onChange={e => setAffectedSites(e.target.value)} style={{ width: '100%', padding: '8px' }} />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label>Date Deployed</label>
                  <input type="date" value={dateDeployed} onChange={e => setDateDeployed(e.target.value)} style={{ width: '100%', padding: '8px' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
                  <button type="submit" style={{ background: '#38a169', color: '#fff', border: 'none', borderRadius: '4px', padding: '8px 16px', cursor: 'pointer', marginRight: '8px' }}>
                    {editingChangelog ? 'Save Changes' : 'Create'}
                  </button>
                  <button type="button" onClick={() => setShowPopup(false)} style={{ background: '#e2e8f0', color: '#2d3748', border: 'none', borderRadius: '4px', padding: '8px 16px', cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showDeleteConfirm && (
          <div className="edit-overlay z-ind-25" style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2001 }}>
            <div style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '8px', width: '400px', textAlign: 'center' }}>
              <h3 style={{ marginTop: 0 }}>Are you sure?</h3>
              <p>This action cannot be undone.</p>
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
                <button
                  onClick={confirmDelete}
                  style={{ background: '#e53e3e', color: '#fff', border: 'none', borderRadius: '4px', padding: '8px 16px', cursor: 'pointer', marginRight: '8px' }}
                >
                  Delete
                </button>
                <button
                  onClick={() => { setShowDeleteConfirm(false); setDeletingId(null); }}
                  style={{ background: '#e2e8f0', color: '#2d3748', border: 'none', borderRadius: '4px', padding: '8px 16px', cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChangelogsPage; 